import uniq from 'lodash/uniq';

import {
  isCohortSegment,
  isDateSegment,
  isUserSegment,
  Segment,
} from 'common/query-builder/query-block/segment-menu/types';
import {QueryInterface, RetentionQuery} from 'common/report/retention/models/retention-query';
import {SegmentLiftBaselineOption} from 'common/report/util/lift/types';
import {generateCohortBaselineOptions, isLiftEnabled} from 'common/report/util/lift';
import {SegmentKeyToPath} from 'common/report/retention/models/types';
import {ChartType, ChartVizType, ValueRepresentationType} from 'common/report/constants';
import {SectionItem} from 'common/components/items-menu/types';
import {FullLengthDateRange} from 'common/widgets/date-range-picker/models/full-length-date-range';
import {getLiftTypeOverridesForRetention} from 'common/report/retention/util/lift';
import {ChartHeaderAttrs, ChartHeaderUpdateEvent} from 'common/widgets/chart-header/types';
import {NON_INTERVALIZED_VALUE, RetentionBookmarkParams, RetentionChartType} from 'common/report/retention/util/types';
import {ChartHeaderUpdateDetail} from 'common/widgets/chart-header';
import {CohortType} from 'common/report/retention/models/types';
import {Cohort} from 'common/query-builder/cohorts-query-builder/models/cohort';
import {
  ReportBasedCohorts,
  SerializedAddictionReport,
  SerializedAddictionReportBehavior,
  SerializedAddictionReportBehaviorParam,
  SerializedAddictionReportWrapper,
  SerializedRetentionReportBehavior,
  SerializedRetentionReportBehaviorParam,
  SerializedRetentionReportWrapper,
} from 'common/query-builder/cohorts-query-builder/models/types/serialized-cohort-types';
import {Expression} from 'common/query-builder/cohorts-query-builder/models/types/expression-types';
import {ArithmeticOperator, Property} from 'common/query-builder/cohorts-query-builder/models/enums/expression-enums';
import {SpecialTimeUnit} from 'common/widgets/date-range-picker/models/types';
import {getCalendarDate, parseDatetimeRange, parseSpecialDate} from 'common/util/date';
import {useRetentionConsistency} from 'common/report/util/feature-sampling';

function getBreakdowns(query: RetentionQuery): Segment[] {
  return query.groupBy || [];
}

function getLeafLevelSegments(query: RetentionQuery, segmentKeyToPath: SegmentKeyToPath): SegmentLiftBaselineOption[] {
  const breakdowns = getBreakdowns(query);
  if (!breakdowns.length) {
    return [];
  }

  // We used to call groupByToBookmarkGroupBy, but breakdowns is already converted, at least when cohorts are concerned
  // cohorts were being duplicated erroneously, because they show up here with the negated copies already present
  const lastBreakdown = breakdowns[breakdowns.length - 1];
  if (isCohortSegment(lastBreakdown)) {
    return generateCohortBaselineOptions(lastBreakdown);
  } else if (segmentKeyToPath) {
    return uniq(
      Object.entries(segmentKeyToPath).map((value) => {
        // value looks like: [`root/leaf`, [`root`,`leaf`]]
        return value[1].slice(-1)[0];
      }),
    ).map((propertyValue) => {
      return {displayName: propertyValue, propertyValue};
    });
  } else {
    return [];
  }
}

// eventually we will remove extra complexity after we release retention consistency changes
// for now keeping those inline checks here is easier to follow the logic
// eslint-disable-next-line sonarjs/cognitive-complexity
export function getRetentionChartHeaderAttrs(
  query: RetentionQuery,
  segmentKeyToPath: null | SegmentKeyToPath,
  workspaceId: null | number,
): Partial<ChartHeaderAttrs> {
  const isAddiction = query.isAddiction();

  let chartTypeOverrides: Partial<Record<ChartType, SectionItem>> = {};
  if (isAddiction) {
    chartTypeOverrides = {
      [ChartType.RetentionTable]: {label: `Frequency Curve`},
      [ChartType.RetentionTrend]: {label: useRetentionConsistency() ? `Line` : `Frequency Trends`},
    };
  } else {
    if (query.timeComparison) {
      chartTypeOverrides = {
        [ChartType.RetentionTable]: {
          label: `Retention Curve`,
          isDisabled: true,
          tooltip: {
            text: `Retention Curve is not available with time comparison.`,
          },
        },
      };
    }
  }
  const chartTypes = useRetentionConsistency()
    ? [ChartType.RetentionTable, ChartType.RetentionTrend, ChartType.RetentionTrendMetric]
    : [ChartType.RetentionTable, ChartType.RetentionTrend];

  let chartVizTypes = [];
  let selectedChartVizType;
  let selectedChartType = query.chartType;

  if (query.isRetentionTrendBasedChart()) {
    chartVizTypes = useRetentionConsistency() ? [] : [ChartVizType.Metric, ChartVizType.Line];
    selectedChartVizType = query.chartType === ChartType.RetentionTrend ? ChartVizType.Line : ChartVizType.Metric;
    selectedChartType = useRetentionConsistency()
      ? query.chartType === ChartType.RetentionTrend
        ? ChartType.RetentionTrend
        : ChartType.RetentionTrendMetric
      : ChartType.RetentionTrend;
  }

  // Addiction doesn't have server-calculated %/rate, so we can only do lift on Addiction absolute
  const relativeValueAllowed = !(query.liftComparison && isAddiction);

  const valueRepresentations = relativeValueAllowed
    ? [ValueRepresentationType.Absolute, ValueRepresentationType.Relative]
    : [];
  const selectedValueRepresentation =
    relativeValueAllowed && query.percentageView ? ValueRepresentationType.Relative : ValueRepresentationType.Absolute;

  let timeComparisonDisabledReason = ``;
  if (query.isRetentionCurveBasedChart()) {
    timeComparisonDisabledReason = `Time comparison is not available for ${
      isAddiction ? `Frequency` : `Retention`
    } Curve.`;
  } else if (query.isRetentionTrendBasedChart() && isAddiction) {
    timeComparisonDisabledReason = `Time comparison is not available for Frequency Trend.`;
  }

  return {
    'chart-types': chartTypes,
    'chart-viz-types': chartVizTypes,
    ...(selectedChartVizType ? {'selected-chart-viz-type': selectedChartVizType} : {}),
    'chart-type-overrides': JSON.stringify(chartTypeOverrides),
    'selected-chart-type': selectedChartType,
    'show-date-range-selector': true,
    'date-range': JSON.stringify(FullLengthDateRange.fromBookmarkFormat(query.dateRange)),
    'workspace-id': workspaceId,
    'time-comparison-disabled-reason': timeComparisonDisabledReason,
    'comparison-date-range': query.timeComparison,
    'lift-type-overrides': getLiftTypeOverridesForRetention(query),
    'enable-lift-comparison': isLiftEnabled(),
    breakdowns: getBreakdowns(query),
    'selected-lift-comparison': query.liftComparison,
    'selected-breakdown-segments': segmentKeyToPath ? getLeafLevelSegments(query, segmentKeyToPath) : [],
    ...(useRetentionConsistency()
      ? {}
      : {
          'value-representations': valueRepresentations,
          'selected-value-representation': selectedValueRepresentation,
        }),
  };
}

function convertDateRangeAndUnit(
  dateRange: ChartHeaderUpdateEvent['detail']['dateRange'],
): {
  dateRange: RetentionQuery['dateRange'];
} {
  const convertedDateRange = new FullLengthDateRange(dateRange).getBookmarkFormat();

  return {dateRange: convertedDateRange};
}

function convertValueRepresentation(v: ValueRepresentationType): boolean {
  return v === `relative`;
}

function validateChartType(chartType: ChartHeaderUpdateEvent['detail']['chartType']): RetentionChartType {
  if (
    [
      ChartType.RetentionLine,
      ChartType.RetentionTable,
      ChartType.RetentionTrend,
      ChartType.RetentionTrendMetric,
    ].includes(chartType)
  ) {
    return chartType as RetentionChartType;
  } else {
    throw new Error(`Illegal chart type: ${chartType}`);
  }
}

export function chartHeaderToRetentionTypes(
  update: ChartHeaderUpdateDetail,
  query: RetentionQuery,
): Partial<QueryInterface> {
  const {dateRange, valueRepresentation, chartType, chartVizType} = update.delta;

  if (dateRange) {
    return convertDateRangeAndUnit(dateRange);
  }

  if (valueRepresentation) {
    return {percentageView: convertValueRepresentation(valueRepresentation)};
  }

  if (chartType) {
    return query.createFromChartTypeUpdate(validateChartType(chartType));
  }
  if (chartVizType && query.isRetentionTrendBasedChart()) {
    const newChartType =
      chartVizType === ChartVizType.Metric ? ChartType.RetentionTrendMetric : ChartType.RetentionTrend;
    return query.createFromChartTypeUpdate(validateChartType(newChartType));
  }

  // `comparisonDateRange` is sent when either `comparisonDateRange` OR `liftComparison` have changed in the header
  // This allows us to clear `comparisonDateRange` when the Lift type is changed and vice-versa.
  // In each case, if the key is present, forward the value on. We do this for both, always, contrary to other params above
  // Only absolute value representation makes sense for Lift in Addiction, so we force that here
  let valueRepresentationUpdate = {};
  if (update.delta.liftComparison && query.isAddiction()) {
    valueRepresentationUpdate = {
      percentageView: false,
    };
  }
  return {
    ...(`comparisonDateRange` in update.delta ? {timeComparison: update.comparisonDateRange} : {}),
    ...(`liftComparison` in update.delta ? {liftComparison: update.liftComparison} : {}),
    ...valueRepresentationUpdate,
  };
}

const getBehavior = ({isAddictionReport, isAverageBehavior, rowIdx, columnIdx}) => {
  if (isAddictionReport) {
    const behavior: SerializedAddictionReportBehavior = {
      addiction_index: columnIdx + 1,
    };
    if (!isAverageBehavior) {
      behavior.addiction_interval_index = rowIdx;
    }
    return behavior;
  }

  const behavior: SerializedRetentionReportBehavior = {
    retained_interval_index: columnIdx,
  };
  if (!isAverageBehavior) {
    behavior.birth_interval_index = rowIdx;
  }
  return behavior;
};

const createSegments = ({segments, groupBy}: {segments: string[]; groupBy: Segment[]}) => {
  const modifiedSegments = [];
  groupBy.forEach((group, index) => {
    let segmentValue = segments[index];
    if (isDateSegment(group)) {
      const timeUnit = isUserSegment(group) ? group.user.unit : group.event.unit;
      if (timeUnit === SpecialTimeUnit.HourOfDay || timeUnit === SpecialTimeUnit.DayOfWeek) {
        segmentValue = parseSpecialDate(segmentValue, timeUnit);
      } else {
        const dateRange = parseDatetimeRange(segmentValue, timeUnit, 1);
        const dateTime = Array.isArray(dateRange) ? dateRange[0] : dateRange;
        segmentValue = getCalendarDate(dateTime, true);
      }
    }
    modifiedSegments.push(segmentValue);
  });

  return modifiedSegments;
};

const getBehaviorParams = ({
  isAddictionReport,
  behaviorType,
  behavior,
  segments,
  groupBy,
}: {
  isAddictionReport: boolean;
  behaviorType: CohortType;
  behavior: SerializedRetentionReportBehavior | SerializedAddictionReportBehavior;
  segments: string[];
  groupBy: Segment[];
}) => {
  const modifiedSegments = createSegments({segments, groupBy});
  const behaviorParamsKey = isAddictionReport ? `addicted_behavior` : `retained_behavior`;
  let behaviorParams;
  switch (behaviorType) {
    case CohortType.SpecificIntervalRetained: {
      behaviorParams = [
        {
          [behaviorParamsKey]: behavior,
          segments: modifiedSegments,
        },
      ];
      break;
    }
    case CohortType.SpecificIntervalDropOff:
      behaviorParams = [
        {
          drop_off_behavior: behavior,
          segments: modifiedSegments,
        },
      ];
      break;
  }
  if (isAddictionReport) {
    return behaviorParams as SerializedAddictionReportBehaviorParam[];
  }
  return behaviorParams as SerializedRetentionReportBehaviorParam[];
};

const getReportWrapper = ({
  reportType,
  segments,
  bookmark,
  groupedRowIdx,
  columnIdx,
  behaviorType,
}: {
  reportType: string;
  segments: string[];
  bookmark: RetentionBookmarkParams;
  groupedRowIdx: number;
  columnIdx: number;
  behaviorType: CohortType;
}) => {
  const rowIdx = Number.isFinite(groupedRowIdx) ? groupedRowIdx : 0;
  const isAverageBehavior = bookmark.date_unit !== NON_INTERVALIZED_VALUE && !Number.isFinite(groupedRowIdx);
  const isAddictionReport = reportType === `addiction-cumulative` || reportType === `addiction-non-cumulative`;
  const cumulative = reportType === `addiction-cumulative`;
  const groupBy = bookmark.group_by || [];

  const behavior = getBehavior({isAddictionReport, isAverageBehavior, rowIdx, columnIdx});
  const behaviorParams = getBehaviorParams({isAddictionReport, behaviorType, behavior, segments, groupBy});
  const report = {
    bookmark,
    ...(isAverageBehavior
      ? // Creating a cohort from the average row will include all other rows. `average_behavior_params` will
        // be resolved to a list of `behavior_params` at query time.
        {average_behavior_params: behaviorParams}
      : {behavior_params: behaviorParams}),
  };

  if (isAddictionReport) {
    (report as SerializedAddictionReport).cumulative = cumulative;
    return {
      [ReportBasedCohorts.Addiction]: report,
    } as SerializedAddictionReportWrapper;
  }

  return {
    [ReportBasedCohorts.Retention]: report,
  } as SerializedRetentionReportWrapper;
};

const selector: Expression = {
  operator: ArithmeticOperator.Equals,
  children: [
    {
      property: Property.Behaviors,
      value: `A0`,
    },
    {
      property: Property.Literal,
      value: true,
    },
  ],
};

export function createCohort({
  name,
  description,
  isSegmented,
  bookmark,
  groupedRowIdx,
  columnIdx,
  segments,
  behaviorType,
  reportType,
}: {
  name: string;
  description: string;
  isSegmented: boolean;
  bookmark: RetentionBookmarkParams;
  groupedRowIdx: number;
  columnIdx: number;
  segments: string[];
  behaviorType: CohortType;
  reportType: string;
}): Cohort {
  const behaviors = {
    A0: getReportWrapper({
      reportType,
      segments: isSegmented ? segments : [],
      bookmark,
      groupedRowIdx,
      columnIdx,
      behaviorType,
    }),
  };
  return Cohort.parseCohort({
    behaviors,
    name,
    description,
    selector,
    ...(bookmark.data_group_id ? {data_group_id: bookmark.data_group_id} : {}),
  });
}
